"use client";

import { printedRef } from "../scan/scan-target";
import { useEffect, useState } from "react";

/** The SAADS Scan flow from a page: `scan(printed)` asks (printed: the paper carries a generated form's code) for a pass for `kind:ref`, opens the app and keeps `pass` set while waiting; every two seconds the pass is asked about, and `onDone` runs when the file arrived. `problem` is "failed" or "expired" when it did not work; `abandon()` withdraws the pass. */
export function useScanPass(kind: string, ref: string, label: string, onDone: () => void) {
  const [pass, setPass] = useState<string | null>(null);
  const [problem, setProblem] = useState<"failed" | "expired" | null>(null);

  useEffect(() => {
    if (!pass) return;
    const timer = setInterval(async () => {
      const state = ((await fetch(`/api/documents/scan/status/${pass}`).then((r) => r.json()).catch(() => null)) as { state?: string } | null)?.state;
      if (state === "done") { setPass(null); onDone(); }
      else if (state === "expired" || state === "abandoned") { setPass(null); setProblem("expired"); }
    }, 2000);
    return () => clearInterval(timer);
  }, [pass, onDone]);

  async function scan(printed = false, answers = "", redact = false, asCode = "") {
    setProblem(null);
    const res = await fetch("/api/documents/scan/token", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ kind, ref: printedRef(answers ? `${ref}#${answers}` : ref, printed, redact, asCode), label }) }).catch(() => null);
    const made = res?.ok ? ((await res.json()) as { id?: string; link?: string }) : null;
    if (!made?.id || !made.link) return setProblem("failed");
    setPass(made.id);
    window.location.href = made.link;
  }

  async function abandon() {
    const id = pass;
    setPass(null);
    await fetch("/api/documents/scan/abandon", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ id }) }).catch(() => null);
  }

  return { pass, problem, scan, abandon, clear: () => setProblem(null) };
}
